import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';

/**
 * The review screen's confirmations.
 *
 * Every destructive or public-facing action passes through one of these, for a
 * single template or for a selection: rejecting and requesting revisions take
 * a note, deleting and publishing take a deliberate second click.
 */

const names = (targets) => (targets.length === 1 ? `“${targets[0].name}”` : `${targets.length} templates`);

/** A decision that carries a note: reject, or request revisions. */
export const NoteDialog = ({ kind, targets, busy, onCancel, onConfirm }) => {
  const [note, setNote] = useState('');
  useEffect(() => { setNote(''); }, [kind, targets]);
  if (!targets) return null;
  const reject = kind === 'reject';
  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{reject ? 'Reject' : 'Request revisions for'} {names(targets)}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-600">
          {reject
            ? 'Rejected templates stay private. The reason is kept with the template.'
            : 'The template goes back for changes and stays private until it is resubmitted and approved.'}
        </p>
        <textarea
          autoFocus
          rows={4}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={reject ? 'Reason for rejection (optional)' : 'What should change?'}
          className="w-full mt-1 p-3 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-violet-300"
        />
        <div className="flex gap-3 pt-1">
          <Button variant="outline" className="flex-1" onClick={onCancel}>Cancel</Button>
          <Button
            className={`flex-1 text-white ${reject ? 'bg-red-600 hover:bg-red-700' : 'bg-sky-600 hover:bg-sky-700'}`}
            disabled={busy || (!reject && !note.trim())}
            onClick={() => onConfirm(note.trim() || null)}
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
            {reject ? 'Reject' : 'Send back'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** A second click before deleting or publishing. */
export const ConfirmDialog = ({ kind, targets, busy, onCancel, onConfirm }) => {
  if (!targets) return null;
  const copy = {
    delete: {
      title: `Delete ${names(targets)}?`,
      body: 'Deleted templates are removed from every list. This cannot be undone from this screen.',
      action: 'Delete', tone: 'bg-red-600 hover:bg-red-700',
    },
    publish: {
      title: `Publish ${names(targets)}?`,
      body: 'Published templates are visible to everyone in the public marketplace immediately.',
      action: 'Publish', tone: 'bg-emerald-600 hover:bg-emerald-700',
    },
  }[kind];
  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>{copy.title}</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-600">{copy.body}</p>
        <div className="flex gap-3 pt-1">
          <Button variant="outline" className="flex-1" onClick={onCancel}>Cancel</Button>
          <Button className={`flex-1 text-white ${copy.tone}`} disabled={busy} onClick={onConfirm}>
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}{copy.action}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
    {children}
  </label>
);

const inputClass = 'w-full h-9 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-300';

/** Edit a template's name, taxonomy, style, description and tags. */
export const MetadataDialog = ({ template, categories, styles, busy, onCancel, onSave }) => {
  const [form, setForm] = useState(null);
  useEffect(() => {
    setForm(template ? {
      name: template.name || '',
      category: template.category || '',
      subcategory: template.subcategory || '',
      style: template.style || '',
      description: template.description || '',
      tags: (template.tags || []).join(', '),
    } : null);
  }, [template]);
  if (!template || !form) return null;
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const save = () => onSave({
    name: form.name.trim(),
    category: form.category.trim(),
    subcategory: form.subcategory.trim(),
    style: form.style.trim(),
    description: form.description.trim(),
    tags: form.tags.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean),
  });

  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Edit template details</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Name"><input className={inputClass} value={form.name} onChange={set('name')} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <input className={inputClass} list="review-categories" value={form.category} onChange={set('category')} />
              <datalist id="review-categories">{categories.map((c) => <option key={c.category} value={c.category} />)}</datalist>
            </Field>
            <Field label="Subcategory"><input className={inputClass} value={form.subcategory} onChange={set('subcategory')} /></Field>
          </div>
          <Field label="Style">
            <input className={inputClass} list="review-styles" value={form.style} onChange={set('style')} />
            <datalist id="review-styles">{styles.map((s) => <option key={s.style} value={s.style} />)}</datalist>
          </Field>
          <Field label="Description">
            <textarea rows={3} className={`${inputClass} h-auto py-2 resize-none`} value={form.description} onChange={set('description')} />
          </Field>
          <Field label="Tags (comma separated)"><input className={inputClass} value={form.tags} onChange={set('tags')} /></Field>
        </div>
        <div className="flex gap-3 pt-2">
          <Button variant="outline" className="flex-1" onClick={onCancel}>Cancel</Button>
          <Button className="flex-1 bg-violet-600 hover:bg-violet-700 text-white" disabled={busy || !form.name.trim()} onClick={save}>
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}Save details
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
